import { motion } from "framer-motion";
import useLanyard from "@/hooks/useLanyard";

const DISCORD_ID = "1155743565968855060";

const STATUS_COLORS = {
  online: "bg-green-400",
  idle: "bg-yellow-400",
  dnd: "bg-red-500",
  offline: "bg-white/30",
};

const STATUS_LABEL = {
  online: "ONLINE",
  idle: "IDLE",
  dnd: "DO NOT DISTURB",
  offline: "OFFLINE",
};

function getAvatarUrl(user) {
  if (!user) return null;
  if (user.avatar) {
    return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.${user.avatar.startsWith("a_") ? "gif" : "png"}?size=256`;
  }
  return `https://cdn.discordapp.com/embed/avatars/${Number(user.discriminator || 0) % 5}.png`;
}

function getAssetUrl(applicationId, asset) {
  if (!asset) return null;
  if (asset.startsWith("mp:external/")) {
    return `https://media.discordapp.net/external/${asset.replace("mp:external/", "")}`;
  }
  if (asset.startsWith("spotify:")) {
    return `https://i.scdn.co/image/${asset.split(":")[1]}`;
  }
  return `https://cdn.discordapp.com/app-assets/${applicationId}/${asset}.png`;
}

export default function LanyardCard() {
  const { data, connected } = useLanyard(DISCORD_ID);
  const status = data?.discord_status || "offline";
  const user = data?.discord_user;
  const activity =
    data?.activities?.find((a) => a.type !== 4) || null;
  const customStatus = data?.activities?.find((a) => a.type === 4);
  const spotify = data?.spotify;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
      className="relative border border-white/10 bg-[#0d0d0f] p-6 md:p-8 clip-tactical group noise-overlay glow-border h-full"
      data-testid="lanyard-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-6">
        <span>[ LIVE // DISCORD.PRESENCE ]</span>
        <span className={`flex items-center gap-2 ${connected ? "text-green-400" : "text-red-400"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-green-400 pulse-dot" : "bg-red-500"}`} />
          {connected ? "WS.OK" : "RECONNECTING"}
        </span>
      </div>

      {/* User */}
      <div className="flex items-center gap-5">
        <div className="relative shrink-0">
          <div className="w-20 h-20 border border-white/15 overflow-hidden bg-[#141416]">
            {user ? (
              <img
                src={getAvatarUrl(user)}
                alt={user.username}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-white/30 font-mono text-xs">
                ---
              </div>
            )}
          </div>
          <span
            className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-[#0d0d0f] ${STATUS_COLORS[status]} ${status === "online" ? "pulse-dot" : ""}`}
          />
        </div>

        <div className="min-w-0">
          <div className="font-display font-bold text-2xl leading-none text-white truncate">
            {user?.global_name || user?.username || "Aeoneuh"}
          </div>
          <div className="mt-1 font-mono text-xs text-white/50">
            @{user?.username || "aeoneuh"}
          </div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F47B20]">
            ◈ {STATUS_LABEL[status]}
          </div>
        </div>
      </div>

      {/* Custom status */}
      {customStatus?.state && (
        <div className="mt-5 border-l-2 border-[#F47B20] pl-3 font-mono text-sm text-white/70">
          " {customStatus.state} "
        </div>
      )}

      {/* Divider */}
      <div className="my-6 h-px bg-gradient-to-r from-white/10 via-white/5 to-transparent" />

      {/* Activity / Spotify */}
      {spotify ? (
        <ActivityBlock
          label="LISTENING ON SPOTIFY"
          title={spotify.song}
          subtitle={`by ${spotify.artist}`}
          image={spotify.album_art_url}
          accent="#1DB954"
        />
      ) : activity ? (
        <ActivityBlock
          label={activity.type === 0 ? "PLAYING" : "ACTIVITY"}
          title={activity.name}
          subtitle={activity.details || activity.state || ""}
          image={
            activity.assets?.large_image
              ? getAssetUrl(activity.application_id, activity.assets.large_image)
              : null
          }
          accent="#F47B20"
        />
      ) : (
        <div className="font-mono text-xs text-white/40 uppercase tracking-[0.25em]">
          No activity detected. Standing by.
        </div>
      )}

      <div className="mt-6 font-mono text-[9px] uppercase tracking-[0.3em] text-white/25">
        [ LANYARD.API // WSS ]
      </div>
    </motion.div>
  );
}

function ActivityBlock({ label, title, subtitle, image, accent }) {
  return (
    <div className="flex items-center gap-4">
      {image && (
        <div className="shrink-0">
          <img
            src={image}
            alt={title}
            className="w-14 h-14 border border-white/15 object-cover"
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: accent }}
        >
          {label}
        </div>
        <div className="mt-1 font-display font-bold text-white truncate">{title}</div>
        {subtitle && (
          <div className="mt-0.5 font-mono text-xs text-white/50 truncate">{subtitle}</div>
        )}
      </div>
    </div>
  );
}
